import React, { createContext, useState, useContext, useEffect } from 'react';
import img_a from '../images/clio.jpg';
import img_b from '../images/208.jpg'; 
import img_sandero from '../images/brochure-nouvelle-sandero-2-37.jpg';
import img_opel from '../images/MbRa8Ggs7uwkVNfb.jpg';

// Création du contexte
const CarContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useCars = () => useContext(CarContext);

// Fournisseur du contexte
export const CarProvider = ({ children }) => {
  // Données des voitures
  const [cars] = useState([
    {
      id: 1,
      name: "Renault Clio",
      price: 8500,
      currency: "DA",
      image: img_a,
      description: "Compacte et économique, parfaite pour la ville",
      features: ["5 portes", "Climatisation", "GPS", "Bluetooth"],
      category: "Compacte",
      transmission: "Manuelle",
      fuel: "Essence",
      passengers: 5,
      luggage: "Petit coffre",
      year: 2022,
      available: false
    },
    { 
      id: 2, 
      name: "Peugeot 208", 
      price: 9500, 
      currency: "DA",
      image: img_b,
      description: "Élégante et confortable pour tous vos déplacements",
      features: ["5 portes", "Climatisation", "Écran tactile", "Caméra de recul"],
      category: "Compacte",
      transmission: "Automatique",
      fuel: "Diesel",
      passengers: 5,
      luggage: "Moyen coffre",
      year: 2023,
      available: false
    },
    { 
      id: 3, 
      name: "Dacia Sandero Stepway", 
      price: 8000, 
      currency: "DA",
      image: img_sandero, 
      description: "Économique et pratique pour tous vos déplacements quotidiens",
      features: [
        "Climatisation", 
        "Écran multimédia", 
        "Start and stop", 
        "Freinage d'urgence"
      ],
      category: "Économique",
      transmission: "Manuelle",
      fuel: "Essence",
      passengers: 5,
      luggage: "Petit coffre",
      year: 2022,
      mileage: "20000 ",
      color: "Blanc",
      available: true
    },
    {
      id: 4,
      name: "Opel Crossland GS",
      price: 9000,
      currency: "DA",
      image: img_opel,
      description: "SUV compact et moderne avec équipements premium",
      features: [
        "Climatisation",
        "Bluetooth",
        "Écran multimédia",
        "Radar et caméra de recul",
        "Commandes au volant",
        "Jantes alliage"
      ],
      category: "SUV",
      transmission: "Manuelle",
      fuel: "Essence",
      passengers: 5,
      luggage: "Moyen coffre",
      year: 2023,
      mileage: "7000",
      color: "Gris",
      power: "110ch",
      available: true
    }
  ]);

  // État des réservations
  const [reservations, setReservations] = useState([]);
  
  // État des favoris
  const [favorites, setFavorites] = useState([]);

  // Charger les réservations et favoris depuis le localStorage au démarrage
  useEffect(() => {
    const savedReservations = localStorage.getItem('carReservations');
    const savedFavorites = localStorage.getItem('carFavorites');
    
    if (savedReservations) {
      setReservations(JSON.parse(savedReservations));
    }
    
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Sauvegarder les réservations dans le localStorage à chaque mise à jour
  useEffect(() => {
    localStorage.setItem('carReservations', JSON.stringify(reservations));
  }, [reservations]);
  
  // Sauvegarder les favoris dans le localStorage à chaque mise à jour
  useEffect(() => {
    localStorage.setItem('carFavorites', JSON.stringify(favorites));
  }, [favorites]);

  // Fonction pour ajouter une réservation
  const addReservation = (reservation) => {
    const newReservation = { 
      ...reservation, 
      id: Date.now(),
      status: 'confirmed',
      createdAt: new Date().toISOString()
    };
    
    setReservations([...reservations, newReservation]);
    return true;
  };

  // Fonction pour obtenir une voiture par son ID
  const getCarById = (id) => {
    return cars.find(car => car.id === parseInt(id));
  };
  
  // Fonction pour ajouter/supprimer des favoris
  const toggleFavorite = (carId) => {
    if (favorites.includes(carId)) {
      setFavorites(favorites.filter(id => id !== carId));
    } else {
      setFavorites([...favorites, carId]);
    }
  };
  
  // Fonction pour vérifier si une voiture est en favoris
  const isFavorite = (carId) => {
    return favorites.includes(carId);
  };
  
  // Fonction pour obtenir les réservations d'un utilisateur
  const getUserReservations = (userId) => {
    return reservations.filter(reservation => reservation.userId === userId);
  };
  
  // Fonction pour annuler une réservation
  const cancelReservation = (reservationId) => {
    const updatedReservations = reservations.map(reservation => 
      reservation.id === reservationId 
        ? { ...reservation, status: 'cancelled' } 
        : reservation
    );
    
    setReservations(updatedReservations);
    return true;
  };

  // Valeur du contexte
  const value = {
    cars,
    reservations,
    favorites,
    addReservation,
    getCarById,
    toggleFavorite,
    isFavorite,
    getUserReservations,
    cancelReservation
  };

  return <CarContext.Provider value={value}>{children}</CarContext.Provider>;
};

export default CarContext;
